import { useCallback } from "react";
import { toastManager } from "@salestrak/ui";
import { useIssueViewMutations } from "#app/hooks/issue-view/use-issue-view-mutations";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { AnalyticsContext, KanbanEvent, useAnalytics } from "#app/lib/analytics";

/**
 * Moves an issue to another issue type through the omnibus `updateIssue`,
 * carrying the values the migrate dialog collected for the target type's
 * required fields. One toast grammar for every surface that changes a type —
 * the detail view, the list's Type cell and the board card. Resolves to
 * whether the server accepted the change.
 *
 * `kanban.change_type` fires here, once per accepted change, so every surface
 * counts the same way. `via` names the surface: "detail", "table_cell",
 * "card" or "menu".
 */
export function useMigrateIssueType() {
  const { updateIssue } = useIssueViewMutations({ updateIssueLabel: "change_type" });
  const { track } = useAnalytics();
  return useCallback(
    async (
      issueId: string,
      issueTypeId: string,
      values: Record<string, unknown> = {},
      via = "unknown",
    ): Promise<boolean> => {
      try {
        const { data } = await updateIssue({
          variables: {
            issueId,
            input: {
              issueTypeId,
              values: Object.keys(values).length > 0 ? values : undefined,
            },
          },
        });
        const result = data?.updateIssue;
        if (result?.__typename === "Issue") {
          track({
            context: AnalyticsContext.Kanban,
            event: KanbanEvent.ChangeType,
            data: { via, requiredValues: Object.keys(values).length },
          });
          return true;
        }
        toastManager.add({
          title:
            result && "message" in result
              ? result.message
              : "Failed to update Project type",
          type: "error",
        });
        return false;
      } catch (err) {
        toastMutationError(err, "Failed to update Project type");
        return false;
      }
    },
    [updateIssue, track],
  );
}
